// ── SVG Canvas Setup, Layer Management, Viewport, Zoom/Pan ─────────

import { state } from './state.ts';

// ── DOM References ─────────────────────────────────────────────────
// flagged: $ returns `any` to avoid cascading TS2339 errors at call sites that access
// DOM-specific properties without a cast (e.g. `.value`, `.checked`). Proper fix: cast
// each call site (e.g. `$(sel) as HTMLInputElement`). Track as a follow-up.
export const $ = (sel: string): any => document.querySelector(sel);
export const $$ = (sel: string) => document.querySelectorAll(sel);

export const svg = () => $("#canvas");
export const container = () => $("#canvasContainer");

export const gridLayer = () => $("#gridLayer");
export const axisLayer = () => $("#axisLayer");
export const boundaryLayer = () => $("#boundaryLayer");
export const treeLayer = () => $("#treeLayer");
export const coverageLayer = () => $("#coverageLayer");
export const heatmapLayer = () => $("#heatmapLayer");
export const zoneLayer = () => $("#zoneLayer");
export const sprinklerLayer = () => $("#sprinklerLayer");
export const labelLayer = () => $("#labelLayer");
export const guideLayer = () => $("#guideLayer");
export const smartGuideLayer = () => $("#smartGuideLayer");
export const pipeLayer = () => $("#pipeLayer");
export const dripLayer = () => $("#dripLayer");
export const valveObjLayer = () => $("#valveObjLayer");
export const weatherStationLayer = () => $("#weatherStationLayer");
export const tempLayer = () => $("#tempLayer");

// ── Coordinate Conversions ─────────────────────────────────────────
export const ORIGIN = { x: 0, y: 0 };

export function metersToSvg(mx: number, my: number) {
  return {
    x: ORIGIN.x + mx * state.pxPerMeter,
    y: ORIGIN.y + my * state.pxPerMeter,
  };
}

export function svgToMeters(sx: number, sy: number) {
  return {
    x: (sx - ORIGIN.x) / state.pxPerMeter,
    y: (sy - ORIGIN.y) / state.pxPerMeter,
  };
}

export function screenToSvg(clientX: number, clientY: number) {
  const svgElem = svg();
  const pt = svgElem.createSVGPoint();
  pt.x = clientX;
  pt.y = clientY;
  const ctm = svgElem.getScreenCTM();
  if (!ctm) return { x: 0, y: 0 };
  const svgPt = pt.matrixTransform(ctm.inverse());
  return { x: svgPt.x, y: svgPt.y };
}

export function screenToMeters(clientX: number, clientY: number) {
  const svgPt = screenToSvg(clientX, clientY);
  return svgToMeters(svgPt.x, svgPt.y);
}

export function snapMeters(m: { x: number; y: number }) {
  if (!state.snapToGrid) return m;
  const g = state.gridSize;
  // Defensive: when gridSize is 0 or negative (e.g. user pasted an invalid
  // value past the input clamp), Math.round(x/0) is ±Infinity/NaN — return
  // raw coordinates so we never produce NaN positions on the canvas.
  if (!Number.isFinite(g) || g <= 0) return m;
  return {
    x: Math.round(m.x / g) * g,
    y: Math.round(m.y / g) * g,
  };
}

// ── SVG Helpers ────────────────────────────────────────────────────
export function svgEl(tag: string, attrs: Record<string, unknown> = {}) {
  const el = document.createElementNS("http://www.w3.org/2000/svg", tag);
  for (const [k, v] of Object.entries(attrs)) {
    el.setAttribute(k, String(v));
  }
  return el;
}

export function clearLayer(layer: Element) {
  while (layer.firstChild) layer.removeChild(layer.firstChild);
}

// ── Viewport ──────────────────────────────────────────────────────
export function updateViewBox() {
  const { x, y, w, h } = state.viewBox;
  svg().setAttribute("viewBox", `${x} ${y} ${w} ${h}`);
}

// ── Zoom ───────────────────────────────────────────────────────────
export function zoomAt(clientX: number, clientY: number, factor: number) {
  const svgPt = screenToSvg(clientX, clientY);
  state.viewBox.x = svgPt.x - (svgPt.x - state.viewBox.x) * factor;
  state.viewBox.y = svgPt.y - (svgPt.y - state.viewBox.y) * factor;
  state.viewBox.w *= factor;
  state.viewBox.h *= factor;
  state.zoomLevel /= factor;
  updateViewBox();
  $("#zoomLevel").textContent = `${Math.round(state.zoomLevel * 100)}%`;
}

export function zoomToFit() {
  const ppm = state.pxPerMeter;
  const pw = Number.isFinite(state.plotWidth) ? state.plotWidth : 0;
  const ph = Number.isFinite(state.plotHeight) ? state.plotHeight : 0;
  // Defensive: if plot dimensions are effectively zero (user cleared the
  // inputs, bad JSON import, etc.) fall back to a safe 10×10m default so we
  // never compute a near-infinite zoom level (was: container.width / tiny).
  if (pw <= 0 || ph <= 0) {
    const fallback = 10 * ppm;
    state.viewBox = {
      x: -ZOOM_FIT_MARGIN, y: -ZOOM_FIT_MARGIN,
      w: fallback + ZOOM_FIT_MARGIN * 2,
      h: fallback + ZOOM_FIT_MARGIN * 2,
    };
    state.zoomLevel = container().clientWidth / state.viewBox.w;
    updateViewBox();
    $("#zoomLevel").textContent = `${Math.round(state.zoomLevel * 100)}%`;
    return;
  }
  state.viewBox = {
    x: -ZOOM_FIT_MARGIN, y: -ZOOM_FIT_MARGIN,
    w: pw * ppm + ZOOM_FIT_MARGIN * 2,
    h: ph * ppm + ZOOM_FIT_MARGIN * 2,
  };
  state.zoomLevel = container().clientWidth / state.viewBox.w;
  updateViewBox();
  $("#zoomLevel").textContent = `${Math.round(state.zoomLevel * 100)}%`;
}

// ── Constants ──────────────────────────────────────────────────────
const ZOOM_FIT_MARGIN = 60;

// ── Smart Guides ────────────────────────────────────────────────────
const SMART_GUIDE_THRESHOLD = 0.15;

/** The element currently being dragged, which must not guide against itself. */
export type SmartGuideExclusion = { type: SmartGuideSource; id: number } | null | undefined;

type SmartGuideSource = "sprinkler" | "tree" | "valveObj" | "weatherStation";

export function findSmartGuides(point: { x: number; y: number }, exclude?: SmartGuideExclusion) {
  const guides = [];
  // Each of these collections has its own id counter, so excluding by a bare
  // id suppressed the alignment guides of every *other* type that happened to
  // share the number — dragging sprinkler #3 silently hid the guides from
  // tree #3, valve object #3 and weather station #3.
  const collections: Array<[SmartGuideSource, Array<{ id: number; x: number; y: number }>]> = [
    ["sprinkler", state.sprinklers],
    ["tree", state.trees],
    ["valveObj", state.valveObjects],
    ["weatherStation", state.weatherStations],
  ];
  for (const [source, coll] of collections) {
    for (const item of coll) {
      if (exclude && exclude.type === source && item.id === exclude.id) continue;
      if (Math.abs(item.x - point.x) < SMART_GUIDE_THRESHOLD) {
        guides.push({ axis: "x", position: item.x, from: item, dist: Math.abs(item.y - point.y) });
      }
      if (Math.abs(item.y - point.y) < SMART_GUIDE_THRESHOLD) {
        guides.push({ axis: "y", position: item.y, from: item, dist: Math.abs(item.x - point.x) });
      }
    }
  }
  for (const g of state.guides) {
    if (g.axis === "x" && Math.abs(g.position - point.x) < SMART_GUIDE_THRESHOLD) {
      guides.push({ axis: "x", position: g.position, manual: true });
    }
    if (g.axis === "y" && Math.abs(g.position - point.y) < SMART_GUIDE_THRESHOLD) {
      guides.push({ axis: "y", position: g.position, manual: true });
    }
  }
  return guides;
}

export function snapToGuides(point: { x: number; y: number }, exclude?: SmartGuideExclusion) {
  if (!state.showGuides) return point;
  const snapped = { x: point.x, y: point.y };
  const guides = findSmartGuides(point, exclude);
  for (const g of guides) {
    if (g.axis === "x") snapped.x = g.position;
    if (g.axis === "y") snapped.y = g.position;
  }
  return snapped;
}

export function findSnapTarget(point: { x: number; y: number }, threshold: number) {
  for (const sp of state.sprinklers) {
    if (Math.hypot(point.x - sp.x, point.y - sp.y) < threshold) {
      return { type: "sprinkler", id: sp.id, x: sp.x, y: sp.y };
    }
  }
  for (const vo of state.valveObjects) {
    if (Math.hypot(point.x - vo.x, point.y - vo.y) < threshold) {
      return { type: "valveObj", id: vo.id, x: vo.x, y: vo.y };
    }
  }
  for (const tree of state.trees) {
    if (tree.type === "waterSource" && Math.hypot(point.x - tree.x, point.y - tree.y) < threshold) {
      return { type: "waterSource", id: tree.id, x: tree.x, y: tree.y };
    }
  }
  return null;
}
